/**
 * 反滥用（Alice 页面 abuse 的复刻）：账号封禁与解封记录。
 *
 * 背景：Alice 列的是自动检测出来的梯度封禁；本仓只有管理员在「用户与计费」手动执行的账号封禁（账号服务的
 *   account_bans 表），接口是 /admin/identity/abuse-bans。没有自动检测、梯度时长与到期自动解封，所以「等级」
 *   列显示空，「解封时间」是手动解封的实际时间，「状态」是生效中或已解除。限流命中、管理员登录失败没有记录。
 * 设计意图：版式与 Alice 一致（三张指标卡、可展开的策略说明、七列表格），策略说明改写成本仓的真实规则。
 * 关键约束：点击一行进入该账号的详情页，在那里解封；本页只读。
 */
import { useState } from 'react'
import { runtime2Api } from '@ap/api-client/admin-runtime2'
import { AG_runtime2 as G, errorText2 } from '@ap/i18n/admin-runtime2'
import { useI18n } from '@ap/i18n'
import { fmtDateTime, navigate, useLoad } from '@ap/ui'
import { AliceBadge, OpsPage, Stat, Stats, TableWrap } from '../../alice/kit.tsx'
import { EmptyRow, Section, ellipsis } from '../../alice/kit-runtime2.tsx'

export function Abuse() {
  const { t, locale } = useI18n()
  const [open, setOpen] = useState(false)
  const { data, error, loading } = useLoad(() => runtime2Api.abuseBans(100), [])
  const bans = data?.bans ?? []
  const dash = t(G.common.dash)

  return (
    <OpsPage eyebrow={t(G.abuse.eyebrow)} title={t(G.abuse.title)} desc={t(G.abuse.desc)} chip={t(G.abuse.chip)}>
      <Stats cols={3}>
        <Stat label={t(G.abuse.statTotal)} value={data ? data.total : dash} />
        <Stat label={t(G.abuse.statActive)} value={data ? data.active : dash} color="error" />
        <Stat label={t(G.abuse.statUsers)} value={data ? data.users : dash} />
      </Stats>

      <Section
        title={t(G.abuse.policyTitle)}
        copy={t(G.abuse.policyCopy)}
        onToggle={() => setOpen((v) => !v)}
        toggle={
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ color: 'var(--text-tertiary)', transition: 'transform .15s', transform: open ? 'rotate(90deg)' : undefined }}
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        }
      >
        {open && (
          <div style={{ padding: '16px 20px', fontSize: 12, lineHeight: 1.8, color: 'var(--text-secondary)' }}>
            {(
              [
                [G.abuse.policy.banTitle, G.abuse.policy.ban],
                [G.abuse.policy.liftTitle, G.abuse.policy.lift],
                [G.abuse.policy.gapTitle, G.abuse.policy.gap],
              ] as const
            ).map(([title, items], i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{t(title)}</div>
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  {items.map((entry, j) => (
                    <li key={j}>{t(entry)}</li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        )}
      </Section>

      <div style={{ marginTop: 16 }}>
        <TableWrap
          head={
            <>
              <th>{t(G.abuse.colUser)}</th>
              <th>{t(G.abuse.colType)}</th>
              <th>{t(G.abuse.colLevel)}</th>
              <th>{t(G.abuse.colBanned)}</th>
              <th>{t(G.abuse.colLifted)}</th>
              <th>{t(G.abuse.colStatus)}</th>
              <th>{t(G.abuse.colReason)}</th>
            </>
          }
        >
          {error ? (
            <EmptyRow cols={7}>{t(G.common.loadFailed, { msg: errorText2(error, locale) })}</EmptyRow>
          ) : loading && !data ? (
            <EmptyRow cols={7}>{t(G.common.loading)}</EmptyRow>
          ) : bans.length === 0 ? (
            <EmptyRow cols={7}>{t(G.abuse.empty)}</EmptyRow>
          ) : (
            bans.map((b) => (
              <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/accounts/${b.account_id}`)}>
                <td>
                  <div style={{ fontWeight: 500 }}>
                    {b.name || b.email}
                    {b.internal && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-tertiary)' }}>({t(G.abuse.internal)})</span>}
                  </div>
                  <div className="mono" style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                    {b.email}
                  </div>
                  <div className="mono" style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                    ID: {b.account_id}
                  </div>
                </td>
                <td style={{ fontSize: 12 }}>{t(G.abuse.typeAdmin)}</td>
                <td>{dash}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(b.banned_at, locale)}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{b.lifted_at ? fmtDateTime(b.lifted_at, locale) : t(G.abuse.awaiting)}</td>
                <td>{b.status === 'active' ? <AliceBadge tone="off">{t(G.abuse.active)}</AliceBadge> : <AliceBadge tone="info">{t(G.abuse.lifted)}</AliceBadge>}</td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 340, ...ellipsis }} title={b.lift_reason ? `${b.reason}\n${t(G.abuse.liftReason, { r: b.lift_reason })}` : b.reason}>
                  {b.reason || dash}
                </td>
              </tr>
            ))
          )}
        </TableWrap>
      </div>
    </OpsPage>
  )
}
